import type { FC, TextareaHTMLAttributes } from 'react';

import cn from 'classnames';

import { FieldStatus, fieldStatusId } from './FieldStatus';

interface LabeledTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id: string;
  label: string;
  // Visually hide the label (still announced to screen readers) when a placeholder is enough.
  hideLabel?: boolean;
  error?: string;
  containerClassName?: string;
}

export const LabeledTextarea: FC<LabeledTextareaProps> = ({
  id,
  label,
  hideLabel = false,
  error,
  className,
  containerClassName,
  rows = 3,
  ...props
}) => {
  const describedBy = fieldStatusId({ id, error });
  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      <label
        htmlFor={id}
        className={cn(hideLabel ? 'sr-only' : 'text-[13px] font-medium leading-4 tracking-[-0.13px] text-black/70')}
      >
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'w-full resize-none rounded-xl border bg-white px-3 py-2 text-sm text-black placeholder:text-black/40 focus:outline-none focus:ring-2 disabled:opacity-60',
          error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-black/10 focus:ring-black/20',
          className,
        )}
        {...props}
      />
      <FieldStatus id={id} error={error} />
    </div>
  );
};
